import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Sparkles,
  AlertOctagon,
  Flame,
  Mail,
  Calendar,
  Briefcase,
  ChevronRight,
  TrendingUp
} from 'lucide-react';
import PageMeta from '../../shared/PageMeta';
import {
  dashboardService,
  type SuperAdminUserPerformance
} from '../../services/dashboardService';
import HealthScoreGauge from './superAdmin/HealthScoreGauge';
import PunchCard from './superAdmin/PunchCard';

type Cadence = 'weekly' | 'monthly';

const SUBMISSION_PILL: Record<'pass' | 'fail' | 'pending', string> = {
  pass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  fail: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
};

function VelocityBars({ series }: { series: Array<{ label: string; assigned: number; completed: number }> }) {
  if (!series.length) return null;
  const max = Math.max(1, ...series.map(s => Math.max(s.assigned, s.completed)));
  return (
    <div className="space-y-2">
      {series.map(s => {
        const aPct = (s.assigned / max) * 100;
        const cPct = (s.completed / max) * 100;
        return (
          <div key={s.label}>
            <div className="mb-0.5 flex items-baseline justify-between">
              <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">{s.label}</span>
              <span className="text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
                <span className="font-bold text-emerald-600 dark:text-emerald-400">{s.completed}</span>
                <span className="mx-1">/</span>
                <span>{s.assigned}</span>
              </span>
            </div>
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700/40">
              <div className="absolute inset-y-0 left-0 rounded-full bg-blue-200 dark:bg-blue-900/40" style={{ width: `${aPct}%` }} />
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${cPct}%` }}
                transition={{ duration: 0.5 }}
                className="absolute inset-y-0 left-0 rounded-full bg-emerald-500"
              />
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-3 pt-2 text-[10px] text-slate-500 dark:text-slate-400">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-300" /> Assigned</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Completed</span>
      </div>
    </div>
  );
}

export default function SuperAdminUserPerformance() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<SuperAdminUserPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cadence, setCadence] = useState<Cadence>('weekly');

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await dashboardService.getSuperAdminUserPerformance(userId);
        setData(res);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load user';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const subScores = useMemo(() => {
    if (!data) return [];
    return [
      { label: 'Completion', value: data.score.completionRate, color: 'bg-emerald-500' },
      { label: 'On-time', value: data.score.onTimeRate, color: 'bg-blue-500' },
      { label: 'Reporting', value: data.score.reportingConsistency, color: 'bg-violet-500' },
      { label: 'Quality', value: data.score.qualityRate, color: 'bg-teal-500' }
    ];
  }, [data]);

  if (loading) {
    return <div className="flex h-[60vh] items-center justify-center"><Sparkles className="h-6 w-6 animate-pulse text-violet-500" /></div>;
  }
  if (error || !data) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-2 text-rose-600">
        <AlertOctagon className="h-6 w-6" />{error ?? 'User not found'}
      </div>
    );
  }

  const lastLogin = data.user.lastLoginAt ? new Date(data.user.lastLoginAt).toLocaleString() : '—';
  const series = cadence === 'weekly' ? data.weeklyVelocity : data.monthlyVelocity;
  const vsRoleText =
    data.score.vsRoleAverage > 0 ? `+${data.score.vsRoleAverage}% vs role avg`
    : data.score.vsRoleAverage < 0 ? `${data.score.vsRoleAverage}% vs role avg`
    : 'on par with role avg';

  return (
    <>
      <PageMeta title={`${data.user.name} · Performance`} description="User performance" />

      <div className="flex flex-col gap-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-violet-600 dark:text-slate-300 dark:hover:text-violet-400"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {/* HERO */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-violet-500/5 via-white to-emerald-500/5 p-5 shadow-sm dark:border-slate-700/50 dark:from-violet-900/10 dark:via-slate-800/60 dark:to-emerald-900/10"
        >
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="relative">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-emerald-500 text-2xl font-bold text-white">
                  {data.user.name.slice(0, 1).toUpperCase()}
                </div>
                {data.user.isOnline && (
                  <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-white bg-emerald-500 dark:border-slate-800" />
                )}
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-400">
                  {data.user.role}{data.user.department ? ` · ${data.user.department}` : ''}
                </p>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{data.user.name}</h1>
                <p className="mt-1 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {data.user.email}</span>
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Last login: {lastLogin}</span>
                </p>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  <span className={`font-semibold ${
                    data.score.vsRoleAverage > 0 ? 'text-emerald-600 dark:text-emerald-400'
                    : data.score.vsRoleAverage < 0 ? 'text-rose-600 dark:text-rose-400'
                    : 'text-slate-500'
                  }`}>{vsRoleText}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 rounded-xl bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400">
                <Flame className="h-5 w-5" />
                <div>
                  <p className="text-xs font-semibold">Streak</p>
                  <p className="text-2xl font-bold tabular-nums">{data.streak.current}<span className="text-sm font-normal text-amber-600/70 dark:text-amber-400/70"> / {data.streak.longest}</span></p>
                </div>
              </div>
              <HealthScoreGauge score={data.score.overall} label="Overall Score" />
            </div>
          </div>

          {/* Sub-scores */}
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {subScores.map(s => (
              <div key={s.label} className="rounded-xl bg-white px-3 py-2.5 shadow-sm dark:bg-slate-800/60">
                <div className="mb-1 flex items-baseline justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{s.label}</span>
                  <span className="text-base font-bold tabular-nums text-slate-900 dark:text-white">{s.value}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700/40">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${s.value}%` }} transition={{ duration: 0.6 }} className={`h-full rounded-full ${s.color}`} />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* PUNCH CARD */}
        <PunchCard data={data.punchCard} />

        {/* VELOCITY + PROJECTS */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10">
                  <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Task Velocity</h3>
              </div>
              <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-[10px] font-semibold dark:border-slate-700 dark:bg-slate-900">
                <button onClick={() => setCadence('weekly')} className={`rounded-md px-2 py-1 transition ${cadence === 'weekly' ? 'bg-white text-violet-600 shadow-sm dark:bg-slate-700 dark:text-violet-400' : 'text-slate-500'}`}>Weekly</button>
                <button onClick={() => setCadence('monthly')} className={`rounded-md px-2 py-1 transition ${cadence === 'monthly' ? 'bg-white text-violet-600 shadow-sm dark:bg-slate-700 dark:text-violet-400' : 'text-slate-500'}`}>Monthly</button>
              </div>
            </div>
            <VelocityBars series={series} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
            <div className="mb-4 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-500/10">
                <Briefcase className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Active Projects</h3>
            </div>
            {data.projects.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-sm text-slate-400">Not assigned to any project</div>
            ) : (
              <ul className="space-y-2">
                {data.projects.map(p => {
                  const rate = p.tasksAssigned > 0 ? Math.round((p.tasksCompleted / p.tasksAssigned) * 100) : 0;
                  return (
                    <li key={p.projectId}>
                      <button
                        onClick={() => navigate(`/dashboard/super-admin/projects/${p.projectId}`)}
                        className="group flex w-full items-center gap-3 rounded-xl border border-slate-200/80 bg-slate-50/40 px-3 py-2 transition hover:border-violet-300 hover:bg-white dark:border-slate-700/50 dark:bg-slate-900/30"
                      >
                        <div className="min-w-0 flex-1 text-left">
                          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{p.projectName}</p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400">{p.tasksCompleted}/{p.tasksAssigned} done · {rate}%</p>
                        </div>
                        <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${rate}%` }} />
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-violet-500" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        </div>

        {/* RECENT SUBMISSIONS */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
          <div className="mb-4 flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10">
              <Calendar className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Recent Submissions</h3>
          </div>
          {data.recentSubmissions.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-sm text-slate-400">No submissions yet</div>
          ) : (
            <ul className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {data.recentSubmissions.map(s => (
                <li key={s.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-slate-50/40 px-3 py-2 dark:border-slate-700/50 dark:bg-slate-900/30">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{s.taskTitle}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{s.projectName} · {s.relativeTime}</p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SUBMISSION_PILL[s.status]}`}>{s.status}</span>
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      </div>
    </>
  );
}
